/**
 * Shared arcade HUD widgets for the monograph mini-games: a draining timer
 * bar, a combo badge, and the keyframes that give the games their flash-game
 * juice. All animation is gated on {@link useReducedMotion} so the
 * `prefers-reduced-motion` setting is honored.
 *
 * Presentation only — these widgets read state, they never compute a verdict.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useEffect, useState } from "react";
import { comboMultiplier } from "./arcadeScoring.js";

/** Tracks the user's `prefers-reduced-motion` preference reactively. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = (): void => {
      setReduced(query.matches);
    };
    update();
    query.addEventListener("change", update);
    return (): void => {
      query.removeEventListener("change", update);
    };
  }, []);
  return reduced;
}

const KEYFRAMES = `
@keyframes arcade-pop { 0% { transform: scale(1); } 40% { transform: scale(1.28); } 100% { transform: scale(1); } }
@keyframes arcade-flash { 0% { opacity: 0; } 30% { opacity: 0.9; } 100% { opacity: 0; } }
@keyframes arcade-shake { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
@keyframes arcade-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }
`;

/** Inject the arcade keyframes once; safe to render in every game panel. */
export function ArcadeKeyframes(): React.JSX.Element {
  return <style>{KEYFRAMES}</style>;
}

export interface TimerBarProps {
  readonly fraction: number;
  readonly remaining: number;
  readonly accent: string;
  readonly danger: string;
  readonly track: string;
  readonly running: boolean;
  readonly reducedMotion: boolean;
}

/** A horizontal countdown bar; turns to the danger color and pulses when low. */
export function TimerBar({
  fraction,
  remaining,
  accent,
  danger,
  track,
  running,
  reducedMotion,
}: TimerBarProps): React.JSX.Element {
  const low = fraction <= 0.25;
  const color = low ? danger : accent;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(Math.max(0, Math.min(1, fraction)) * 100)}
      aria-valuetext={`${remaining.toFixed(0)} seconds remaining`}
      aria-label="Time remaining"
      style={{ display: "flex", alignItems: "center", gap: 10 }}
    >
      <div
        style={{
          position: "relative",
          flex: 1,
          height: 10,
          borderRadius: 999,
          background: track,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            transformOrigin: "left center",
            transform: `scaleX(${String(Math.max(0, Math.min(1, fraction)))})`,
            background: color,
            transition: reducedMotion
              ? "none"
              : "transform 120ms linear, background 200ms ease",
            animation:
              low && running && !reducedMotion
                ? "arcade-pulse 700ms ease-in-out infinite"
                : undefined,
          }}
        />
      </div>
      <span
        style={{
          minWidth: 44,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
          fontWeight: 800,
          fontSize: 13,
          color,
        }}
      >
        {remaining.toFixed(1)}s
      </span>
    </div>
  );
}

export interface ArcadeMeterProps {
  readonly label: string;
  readonly fraction: number;
  readonly color: string;
  readonly className?: string;
  readonly reducedMotion?: boolean;
  readonly style?: React.CSSProperties;
  readonly textColor?: string;
  readonly track?: string;
}

export function ArcadeMeter({
  label,
  fraction,
  color,
  className,
  reducedMotion = false,
  style,
  textColor = "rgba(203,213,225,0.72)",
  track = "rgba(255,255,255,0.10)",
}: ArcadeMeterProps): React.JSX.Element {
  const boundedFraction = Math.max(0, Math.min(1, fraction));
  return (
    <div className={className} style={style}>
      <div
        style={{
          color: textColor,
          display: "flex",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: 10,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 800,
          justifyContent: "space-between",
          marginBottom: 4,
          textTransform: "uppercase",
        }}
      >
        <span>{label}</span>
        <span>{Math.round(boundedFraction * 100)}%</span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(boundedFraction * 100)}
        style={{
          background: track,
          borderRadius: 999,
          height: 8,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: color,
            borderRadius: "inherit",
            height: "100%",
            transition: reducedMotion ? "none" : "width 160ms linear",
            width: `${String(boundedFraction * 100)}%`,
          }}
        />
      </div>
    </div>
  );
}

export interface ComboBadgeProps {
  readonly combo: number;
  readonly color: string;
  readonly muted: string;
  readonly reducedMotion: boolean;
}

/** Shows the current combo and its multiplier; pops when the combo grows. */
export function ComboBadge({
  combo,
  color,
  muted,
  reducedMotion,
}: ComboBadgeProps): React.JSX.Element {
  const active = combo >= 2;
  const multiplier = comboMultiplier(combo);
  return (
    <span
      key={combo}
      aria-label={`Combo ${String(combo)}, ${multiplier.toFixed(1)} times multiplier`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: 999,
        border: `1px solid ${active ? color : muted}66`,
        background: active ? `${color}1A` : "transparent",
        color: active ? color : muted,
        fontWeight: 800,
        fontSize: 12,
        fontVariantNumeric: "tabular-nums",
        animation:
          active && !reducedMotion ? "arcade-pop 260ms ease" : undefined,
      }}
    >
      ⚡ COMBO ×{combo} · {multiplier.toFixed(1)}×
    </span>
  );
}

export interface FlashOverlayProps {
  /** A monotonically increasing token; bump it to replay the flash. */
  readonly token: number;
  readonly color: string;
  readonly reducedMotion: boolean;
}

/** A full-card color flash, replayed whenever `token` changes. */
export function FlashOverlay({
  token,
  color,
  reducedMotion,
}: FlashOverlayProps): React.JSX.Element | null {
  if (reducedMotion || token === 0) return null;
  return (
    <span
      key={token}
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: "inherit",
        background: color,
        opacity: 0,
        pointerEvents: "none",
        animation: "arcade-flash 420ms ease-out both",
      }}
    />
  );
}
